import { ContractPowerOptimizationFeatureService } from '@/features/contract-power-optimization/contract-power-optimization-feature.service';
import { SharedUiColorName } from '@/shared/ui/theming';
import { Component, type OnInit, inject } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslatePipe } from '@ngx-translate/core';
import { combineLatest } from 'rxjs';
import { filter } from 'rxjs/operators';
import { SharedUiColoredTextColorDirective } from '../../../../libs/shared/ui/theming/lib/colors/shared-ui-color.directives';
import { LocalNumberPipe } from '../../../../pipes/localnumber.pipe';
import { VatModes } from '../../../../providers/budgets/budgets.constants';
import { ContractPowerProvider } from '../../../../providers/contract-power/contract-power.provider';
import { DashboardingProvider } from '../../../../providers/dashboarding.provider';
import { ProjectsProvider } from '../../../../providers/projects/projects.provider';
import { LegacyWidgetCode } from '../../../../providers/rights.constants';
import { TrackingService } from '../../../../providers/tracking/tracking.service';
import { WidgetSize } from '../../../../providers/widgets.contants';
import { AbstractWidgetClass } from '../../AbstractWidget.class';
import { MiniWidgetIconCardComponent } from '../mini-widget-icon-card/mini-widget-icon-card';

@UntilDestroy()
@Component({
    styleUrls: ['./widget-mini-contract-optimization.scss'],
    selector: 'widget-mini-contract-optimization',
    templateUrl: 'widget-mini-contract-optimization.html',
    imports: [MiniWidgetIconCardComponent, SharedUiColoredTextColorDirective, LocalNumberPipe, TranslatePipe]
})
export class WidgetMiniContractOptimizationComponent extends AbstractWidgetClass<null, void> implements OnInit {
    private _dashboardingProvider = inject(DashboardingProvider);
    private _contractPowerProvider = inject(ContractPowerProvider);
    private _trackingService = inject(TrackingService);
    private _projectsProvider = inject(ProjectsProvider);
    private _contractPowerOptimizationFeatureService = inject(ContractPowerOptimizationFeatureService);

    size = WidgetSize.THIRD;

    potentialSavings!: number;
    loading: boolean = true;
    statusColor: SharedUiColorName = SharedUiColorName.SUCCESS;
    vatMode!: VatModes;

    ngOnInit() {
        combineLatest(this._dashboardingProvider.getSelectedNodeId$(), this._projectsProvider.getVatMode$())
            .pipe(
                filter(([_, vatMode]) => !!vatMode),
                untilDestroyed(this)
            )
            .subscribe(([classificationNodeId, vatMode]) => {
                this.vatMode = vatMode!;
                this.loading = true;
                this._contractPowerProvider
                    .getBuildingOptimizationTop(classificationNodeId)
                    .then(budgetOptimization => {
                        this.loading = false;

                        // Mixpanel tracking
                        this._trackingService.widgetViewed(LegacyWidgetCode.WDGT_MINI_CONTRACT_OPTIMIZATION, {
                            deliveryCostVatExcl: budgetOptimization.totals.deliveryCostVatExcl,
                            optimizedDeliveryCostVatExcl: budgetOptimization.totals.optimizedDeliveryCostVatExcl,
                            savingsPercentVatExcl: budgetOptimization.totals.savingsPercentVatExcl,
                            savingsVatExcl: budgetOptimization.totals.savingsVatExcl,

                            deliveryCostVatIncl: budgetOptimization.totals.deliveryCostVatIncl,
                            optimizedDeliveryCostVatIncl: budgetOptimization.totals.optimizedDeliveryCostVatIncl,
                            savingsPercentVatIncl: budgetOptimization.totals.savingsPercentVatIncl,
                            savingsVatIncl: budgetOptimization.totals.savingsVatIncl
                        });

                        if (budgetOptimization.totals) {
                            switch (this.vatMode) {
                                case VatModes.VAT_INCL:
                                    this.potentialSavings = budgetOptimization.totals.savingsVatIncl;
                                    break;
                                case VatModes.VAT_EXCL:
                                    this.potentialSavings = budgetOptimization.totals.savingsVatExcl;
                                    break;
                            }
                            if (this.potentialSavings > 0) {
                                this.statusColor = SharedUiColorName.FAILURE;
                            } else {
                                this.statusColor = SharedUiColorName.SUCCESS;
                            }
                        }
                    })
                    .catch(err => {
                        console.error(err);
                    });
            });
    }

    goToOptimizationsPage() {
        this._contractPowerOptimizationFeatureService.goToFeature();
    }
}
